import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type { ElementConfig } from '../ui-element.type';
import type { UIOptionData, UIOptionTextColor } from './ui-option.types';
import { UIOption } from './ui-option';
import styles from './ui-option-multipayment.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIOption}
 * @alias UIOptionMultipayment
 * @element ui-option-multipayment
 * @classdesc Represents a class for <code>ui-option-multipayment</code> element.
 * Similar to ui-option, but holds multibanking account data. Used with
 * <code>ui-dropdown layout="multipayment"</code> and <code>ui-option-list</code>.
 * @property {string} [accountid] {@attr accountid} Account ID.
 * @property {string} [accountnumber] {@attr accountnumber} Account number.
 * @property {string} [holder] {@attr holder} Name of the account holder/owner.
 * @property {string} [bankname] {@attr bankname} Name of the bank.
 * @property {string} [secondarytext] {@attr secondarytext} Secondary text inside placeholder.
 * @property {UIOptionTextColor} [secondarycolor]
 * {@attr secondarycolor} Color for secondary text.
 * @property {string} [tertiarytext] {@attr tertiarytext} Tertiary text inside placeholder.
 * @property {UIOptionTextColor} [tertiarycolor]
 * {@attr tertiarycolor} Color for tertiary text.
 * @slot
 * @example
 * <ui-dropdown layout="multipayment">
 *  <select>
 *   <option data-holder="Tiina Puu" data-account-number="EE111149490" data-account-id="1">
 *   <option data-holder="Mari Muul" data-account-number="EE222249450" data-account-id="2">
 *  </select>
 * </ui-dropdown>
 * @example
 * <ui-option-list label="Select account">
 *   <ui-option-multipayment optgroup text="Personal"></ui-option-multipayment>
 *   <ui-option-multipayment accountnumber="LV30HABA0551054710581" bankname="SEB" selected></ui-option-multipayment>
 *   <ui-option-multipayment accountnumber="EE22224945553922" holder="Mari Muul" bankname="LHV"></ui-option-multipayment>
 * </ui-option-list>
 */

@Component({
    tag: 'ui-option-multipayment',
    styles,
})
export class UIOptionMultipayment extends UIOption {
    @Attribute(String) accountid?: string;
    @Attribute(String) accountnumber?: string;
    @Attribute(String) holder?: string;
    @Attribute(String) bankname?: string;
    @Attribute(String) secondarytext?: string;
    @Attribute(String) secondarycolor?: UIOptionTextColor;
    @Attribute(String) tertiarytext?: string;
    @Attribute(String) tertiarycolor?: UIOptionTextColor;

    /**
     * Finds match between attributes' values and given text.
     * @param {HTMLOptionElement} option
     * @param {string} text
     * @returns {boolean}
     */
    findMatch(option: HTMLOptionElement, text: string): boolean {
        const strings = [
            option.innerText,
            option.getAttribute('value'),
            option.getAttribute('data-holder'),
            option.getAttribute('data-account-number'),
            option.getAttribute('data-bank-name'),
        ];
        return strings.some((str) => !!str?.match(new RegExp(text, 'i')));
    }

    /**
     * Gets data of the multipayment as object.
     * @returns {UIOptionData}
     */
    getData(): UIOptionData {
        return {
            id: this.accountid,
            holder: this.holder,
            bankname: this.bankname,
            accountnumber: this.accountnumber,
        };
    }

    getValue(): string | undefined {
        return this.accountnumber;
    }

    getText(): string | undefined {
        return this.holder;
    }

    /**
     * Build placeholder with selected color.
     * @param {string | undefined} color
     * @param {string} text
     * @returns {ElementConfig}
     * @private
     */
    private buildPlaceholder(
        color: UIOptionTextColor | undefined,
        text: string
    ): ElementConfig {
        return {
            tagName: 'span',
            classList: {
                [`-${color}`]: !!color,
            },
            children: [document.createTextNode(' ' + text)],
        };
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.insertElements(
            (this.optgroup
                ? [this.buildOptGroupLabel()]
                : [
                      {
                          tagName: 'span',
                          attributes: {
                              class: 'ui-option-multipayment__number ui-option__label',
                          },
                          children: [this.accountnumber || ''],
                      },
                      {
                          tagName: 'span',
                          attributes: {
                              class: 'ui-option-multipayment__info',
                          },
                          children: [
                              this.holder ?? '',
                              this.holder && this.bankname ? ' - ' : '',
                              this.bankname ?? '',
                              this.secondarytext
                                  ? this.buildPlaceholder(
                                        this.secondarycolor,
                                        this.secondarytext
                                    )
                                  : undefined,
                              this.tertiarytext
                                  ? this.buildPlaceholder(
                                        this.tertiarycolor,
                                        this.tertiarytext
                                    )
                                  : undefined,
                          ].filter(Boolean),
                      },
                  ]) as Array<HTMLElement | DocumentFragment | ElementConfig>
        );

        super.render();
    }
}
